import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
    Button,
    ButtonGroup,
    Flex,
    Text,
    TextButton,
} from '@zillow/constellation';
import { Row } from '@zillow/zrs-comet';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';
import ModalActions from 'app/shared/actions/ModalActions';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import TourCardSelectable from 'app/shared/components/postingPath/TourCardSelectable';
import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';
import {
    StyledMDActionsContainer,
    StyledMDContainer,
    StyledMDContent,
    StyledMDContentContainer,
} from 'app/shared/containers/ModalContainer/StyledComponents';


import { StyledEvenCol } from './StyledComponents';

const propTypes = {
    defaultTours: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        createdDate: PropTypes.string.isRequired,
        creatorName: PropTypes.string.isRequired,
        imageSrc: PropTypes.string,
        src: PropTypes.string,
        state: PropTypes.oneOf(['active', 'inactive', 'empty']),
    })),
    filterOutTourIds: PropTypes.arrayOf(PropTypes.string),
    shouldLoadTours: PropTypes.bool,
    isListingPublished: PropTypes.bool,
    onLoadTours: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['floorplan', 'unit', 'amenity', 'singleFamily']),
    initialSelectedTourId: PropTypes.string,
};

const defaultProps = {
    defaultTours: [],
    filterOutTourIds: [],
    shouldLoadTours: true,
    isListingPublished: false,
    mode: 'floorplan',
    initialSelectedTourId: null,
};

const HELPER_TEXT = {
    floorplan: 'Select a tour to showcase this floor plan.',
    unit: 'Select a tour to showcase this unit.',
    singleFamily: 'Only one 3d tour can be selected per property.',
};

const HEADER_TEXT = {
    floorplan: 'Floor plan',
    unit: 'Unit',
    amenity: 'Amenity',
    singleFamily: 'Property',
};

const SF_NOT_PUBLISHED_TEXT = 'We won’t able to add this 3d tour to your listing until the listing is live on our '
    + 'sites. Once this listing is live on our sites, please try this operation again.';

const ROW_SIZE = 2;

const onKeyUp = (event) => {
    if (event.keyCode === KeyCodeConstants.ENTER ||
        event.keyCode === KeyCodeConstants.SPACE) {
        event.target.click();
    }
};

const SelectTourModal = ({
    dispatch,
    filterOutTourIds,
    shouldLoadTours,
    isListingPublished,
    onLoadTours,
    onSubmit,
    onCancel,
    mode,
    defaultTours,
    initialSelectedTourId,
}) => {
    const [isLoading, setIsLoading] = React.useState(shouldLoadTours);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [loadingErrorMessage, setLoadingErrorMessage] = React.useState();
    const [errorMessage, setErrorMessage] = React.useState();
    const [tours, setTours] = React.useState(defaultTours);
    const [selectedTourId, setSelectedTourId] = React.useState(initialSelectedTourId);

    React.useEffect(() => {
        if (shouldLoadTours) {
            onLoadTours()
                .then((loadedTours) => {
                    dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.LOAD_TOURS, {
                        label: AnalyticsConstants.TOUR_LOCATION.MODAL + AnalyticsConstants.TOUR_TYPE[mode],
                        value: loadedTours.length,
                    }));
                    // Remove already selected amenity tours except the initialSelectedTourId
                    const filteredTours = mode === 'amenity'
                        ? loadedTours.filter(tour => tour.id === initialSelectedTourId
                            || filterOutTourIds.indexOf(tour.id) === -1)
                        : loadedTours;
                    setTours(filteredTours);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                    setLoadingErrorMessage('Cannot retrieve 3d tours, please try again later.');
                });
        }
    }, [dispatch, mode, onLoadTours, shouldLoadTours]);

    const handleSubmit = () => {
        setIsSubmitting(true);
        if (mode === 'singleFamily' && !isListingPublished) {
            setErrorMessage(SF_NOT_PUBLISHED_TEXT);
            return;
        }
        return onSubmit(selectedTourId)
            .catch(() => {
                setIsSubmitting(false);
                setErrorMessage('Cannot associate this tour, please try again later.');
            });
    };

    const handleSelectTourId = (id) => {
        const isUnselecting = selectedTourId === id;
        const gaEvent = isUnselecting
            ? AnalyticsConstants.GA_EVENTS.UNSELECT_TOUR
            : AnalyticsConstants.GA_EVENTS.SELECT_TOUR;
        dispatch(analyticsUtils.track.GAEventV2(gaEvent, {
            label: AnalyticsConstants.TOUR_LOCATION.MODAL + AnalyticsConstants.TOUR_TYPE[mode],
        }));
        setSelectedTourId(isUnselecting ? null : id);
    };

    const handleViewTour = (tour) => {
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.VIEW_TOUR, {
            label: AnalyticsConstants.TOUR_LOCATION.MODAL,
        }));

        const onBackClick = () => {
            dispatch(ModalActions.showModal({
                header: `${HEADER_TEXT[mode]} tour`,
                modalType: ModalConstants.SELECT_TOUR_MODAL,
                onCancel: () => dispatch(ModalActions.hideModal()),
                onSubmit,
                isListingPublished,
                defaultTours: tours,
                mode,
                initialSelectedTourId: selectedTourId,
                shouldLoadTours: false,
                isFluid: false,
            }));
        };
        dispatch(ModalActions.showModal({
            header: tour.name,
            modalType: ModalConstants.VIEW_TOUR_MODAL,
            src: tour.src,
            title: tour.name,
            isFluid: true,
            footer: () => <Button onClick={onBackClick}>Back</Button>,
        }));
    };

    if (isLoading) {
        return <SpinnerContainer />;
    }

    if (loadingErrorMessage) {
        return <Text fontColor="caution">{loadingErrorMessage}</Text>;
    }

    if (!isLoading && tours.length === 0) {
        return <Text>There are no 3d tours associated with this listing.</Text>;
    }

    const tourChunks = [];
    for (let i = 0; i < tours.length; i += ROW_SIZE) {
        tourChunks.push(tours.slice(i, i + ROW_SIZE));
    }

    return (
        <StyledMDContainer>
            <StyledMDContentContainer>
                <StyledMDContent fullWidth={true}>
                    {HELPER_TEXT[mode] && (
                        <Text paddingBottom="xs" fontType="body" as="div">
                            {HELPER_TEXT[mode]}
                        </Text>
                    )}
                    {tourChunks.map((tourChunk, chunkIndex) => (
                        <Row paddingY="xs" key={`tour-row-${chunkIndex}`}>
                            {tourChunk.map((tour, tourIndex) => (
                                <StyledEvenCol
                                    key={tour.id}
                                    even={tourIndex % 2 === 0}
                                    md={6}
                                >
                                    <TourCardSelectable
                                        tour={tour}
                                        isSelected={tour.id === selectedTourId}
                                        onView={() => handleViewTour(tour)}
                                        onSelect={() => handleSelectTourId(tour.id)}
                                    />
                                </StyledEvenCol>
                            ))}
                        </Row>
                    ))}
                    <Text fontColor="caution">{errorMessage}</Text>
                </StyledMDContent>
            </StyledMDContentContainer>
            <StyledMDActionsContainer>
                <Flex display="flex" flexDirection="column" alignItems="flex-end">
                    <ButtonGroup aria-label="controls">
                        <TextButton
                            aria-label="Cancel"
                            tabIndex={0}
                            onClick={onCancel}
                            onKeyUp={onKeyUp}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </TextButton>
                        <Button
                            onClick={handleSubmit}
                            buttonType="primary"
                            disabled={isSubmitting}
                        >
                            Save
                        </Button>
                    </ButtonGroup>
                </Flex>
            </StyledMDActionsContainer>
        </StyledMDContainer>
    );
};

SelectTourModal.propTypes = propTypes;
SelectTourModal.defaultProps = defaultProps;

export { SelectTourModal };
export default connect()(SelectTourModal);
